/**
 * InlineNotice — Componente padronizado de mensagens inline
 * (advertência, informação, sucesso, erro).
 *
 * Padrão visual do sistema:
 *  - SEM fundo colorido (mantém transparente)
 *  - Barra lateral esquerda colorida conforme o tipo
 *  - Ícone colorido conforme o tipo
 *  - Texto em cor padrão (foreground), garantindo legibilidade
 *
 * Uso:
 *   <InlineNotice variant="warning">Mensagem...</InlineNotice>
 *   <InlineNotice variant="info" icon={Info}>Mensagem...</InlineNotice>
 *
 * RELEASE: 2026-06-09 v1.0.0 - Criação do padrão unificado
 */

import * as React from 'react';
import { AlertTriangle, Info, CheckCircle2, XCircle, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export type InlineNoticeVariant = 'warning' | 'info' | 'success' | 'error';

interface InlineNoticeProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Tipo da mensagem — define cor da barra e do ícone */
  variant?: InlineNoticeVariant;
  /** Ícone customizado (opcional). Por padrão, escolhe pelo variant */
  icon?: LucideIcon;
  /** Conteúdo da mensagem */
  children: React.ReactNode;
}

// Mapeamento de cores (barra lateral + ícone) por variante
const VARIANT_STYLES: Record<InlineNoticeVariant, { bar: string; icon: string; iconCmp: LucideIcon }> = {
  warning: {
    bar: 'border-l-amber-500 dark:border-l-amber-400',
    icon: 'text-amber-500 dark:text-amber-400',
    iconCmp: AlertTriangle,
  },
  info: {
    bar: 'border-l-blue-500 dark:border-l-blue-400',
    icon: 'text-blue-500 dark:text-blue-400',
    iconCmp: Info,
  },
  success: {
    bar: 'border-l-emerald-500 dark:border-l-emerald-400',
    icon: 'text-emerald-500 dark:text-emerald-400',
    iconCmp: CheckCircle2,
  },
  error: {
    bar: 'border-l-destructive',
    icon: 'text-destructive',
    iconCmp: XCircle,
  },
};

/**
 * Mensagem inline padronizada (advertência/info/sucesso/erro).
 * Visual: sem fundo, barra lateral colorida, ícone colorido, texto em foreground.
 */
export function InlineNotice({
  variant = 'warning',
  icon,
  className,
  children,
  ...rest
}: InlineNoticeProps) {
  const styles = VARIANT_STYLES[variant];
  const Icon = icon ?? styles.iconCmp;

  return (
    <div
      role="note"
      className={cn(
        // Sem fundo; barra lateral colorida; texto preto/foreground
        'flex items-start gap-2 border-l-4 bg-transparent px-3 py-2 text-sm text-foreground',
        styles.bar,
        className,
      )}
      {...rest}
    >
      <Icon className={cn('h-4 w-4 shrink-0 mt-0.5', styles.icon)} />
      <div className="flex-1">{children}</div>
    </div>
  );
}
